/**
 * HTTP client of the P2P signaling server (`apps/signaling`).
 *
 * It goes against the SAME origin (`/api/p2p`): in dev/e2e Vite's proxy
 * forwards it to the local binary (port 15788). In a real deployment you have to
 * publish the binary behind the same origin or inject the URL via
 * `window.__APP_CONFIG__.p2pSignalingUrl` (see LOCAL_MULTIPLAYER_GUIDE).
 *
 * Responses are VALIDATED structurally (without type assertions): a half-
 * deployed server or a proxy that returns HTML must not slip through. Payloads and
 * tokens are never logged.
 */

import { isRecord } from "@enviar/core";

declare global {
  interface Window {
    /** Config baked per environment (deploy-web-pages) — optional in dev. */
    __APP_CONFIG__?: { p2pSignalingUrl?: string };
  }
}

export type SignalingErrorKind =
  | "not-found" // invalid code or expired room (server does not distinguish)
  | "already-used" // code already consumed by another guest
  | "rate-limited"
  | "unavailable"; // network down, server not deployed or corrupt response

export class SignalingError extends Error {
  override readonly name = "SignalingError";
  readonly kind: SignalingErrorKind;

  constructor(kind: SignalingErrorKind) {
    super(`signaling: ${kind}`);
    this.kind = kind;
  }
}

function baseUrl(): string {
  // `__APP_CONFIG__` is typed via `declare global { interface Window }` above;
  // `globalThis` does not inherit it (TS7017), so `window` is kept here on purpose.
  // oxlint-disable-next-line unicorn/prefer-global-this
  return window.__APP_CONFIG__?.p2pSignalingUrl ?? "/api/p2p";
}

function kindForStatus(status: number): SignalingErrorKind {
  if (status === 404) {
    return "not-found";
  }
  if (status === 409) {
    return "already-used";
  }
  if (status === 429) {
    return "rate-limited";
  }
  return "unavailable";
}

async function request(path: string, init: RequestInit & { token?: string }): Promise<unknown> {
  const { token, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set("content-type", "application/json");
  if (token) {
    headers.set("x-p2p-token", token);
  }
  let response: Response;
  try {
    response = await fetch(`${baseUrl()}${path}`, { ...rest, headers });
  } catch {
    throw new SignalingError("unavailable");
  }
  if (!response.ok) {
    throw new SignalingError(kindForStatus(response.status));
  }
  try {
    const body: unknown = await response.json();
    return body;
  } catch {
    throw new SignalingError("unavailable");
  }
}

interface CreatedRoom {
  code: string;
  hostToken: string;
  ttlSecs: number;
}

export async function createRoom(ttlSecs?: number): Promise<CreatedRoom> {
  const body = await request("/rooms", {
    method: "POST",
    body: JSON.stringify(ttlSecs === undefined ? {} : { ttlSecs }),
  });
  if (
    !isRecord(body) ||
    typeof body.code !== "string" ||
    typeof body.hostToken !== "string" ||
    typeof body.ttlSecs !== "number"
  ) {
    throw new SignalingError("unavailable");
  }
  return { code: body.code, hostToken: body.hostToken, ttlSecs: body.ttlSecs };
}

export async function joinRoom(code: string): Promise<string> {
  const body = await request(`/rooms/${encodeURIComponent(code)}/join`, {
    method: "POST",
    body: "{}",
  });
  if (!isRecord(body) || typeof body.guestToken !== "string") {
    throw new SignalingError("unavailable");
  }
  return body.guestToken;
}

export async function postSignal(code: string, token: string, payload: string): Promise<void> {
  await request(`/rooms/${encodeURIComponent(code)}/messages`, {
    method: "POST",
    token,
    body: JSON.stringify({ payload }),
  });
}

interface FetchedSignals {
  messages: { seq: number; payload: string }[];
  peerJoined: boolean;
}

export async function fetchSignals(
  code: string,
  token: string,
  after: number,
): Promise<FetchedSignals> {
  const body = await request(`/rooms/${encodeURIComponent(code)}/messages?after=${after}`, {
    method: "GET",
    token,
  });
  if (!isRecord(body) || !Array.isArray(body.messages) || typeof body.peerJoined !== "boolean") {
    throw new SignalingError("unavailable");
  }
  const messages: { seq: number; payload: string }[] = [];
  for (const entry of body.messages) {
    if (isRecord(entry) && typeof entry.seq === "number" && typeof entry.payload === "string") {
      messages.push({ seq: entry.seq, payload: entry.payload });
    }
  }
  return { messages, peerJoined: body.peerJoined };
}

export async function closeRoom(code: string, token: string): Promise<void> {
  try {
    await request(`/rooms/${encodeURIComponent(code)}/close`, {
      method: "POST",
      token,
      body: "{}",
    });
  } catch {
    // Best effort: the room expires on its own by TTL.
  }
}
